import type { CSSProperties, ReactNode } from "react";
import { helpers } from "../../util/helpers.ts";
import { useLocalPartial } from "../../util/local.ts";
import {
  mobileNavigationItems,
  resolveMobileSection,
  type MobileNavigationIcon,
  type MobileNavigationContext,
} from "../../util/mobileNavigation.ts";
import { parseMobileReturnState } from "../../util/mobilePageContext.ts";
import { getMobileTabRedDots } from "../../util/mobileTabAlerts.ts";
import { useViewData } from "../../util/viewManager.tsx";
import i18n from "../../util/i18n.ts";

const bottomTabBarStyle: CSSProperties = {
  // Keep safe-area inset bottom for Capacitor home-indicator clearance.
  minHeight: "var(--mobile-bottom-nav-height)",
  paddingBottom: "env(safe-area-inset-bottom, 0px)",
};

const bottomTabIconPaths: Record<MobileNavigationIcon, ReactNode> = {
  games: (
    <>
      <path d="M7 3v3" />
      <path d="M17 3v3" />
      <path d="M4 8h16" />
      <rect height="16" rx="2.5" width="16" x="4" y="5" />
      <path d="M8 12h3" />
      <path d="M13 12h3" />
      <path d="M8 16h3" />
    </>
  ),
  home: (
    <>
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5.5 10v10h13V10" />
      <path d="M9.5 20v-5h5v5" />
    </>
  ),
  league: (
    <>
      <path d="M6 5h12" />
      <path d="M6 10h12" />
      <path d="M6 15h8" />
      <path d="M6 20h12" />
    </>
  ),
  market: (
    <>
      <path d="M5 8h12" />
      <path d="m14 5 3 3-3 3" />
      <path d="M19 16H7" />
      <path d="m10 13-3 3 3 3" />
    </>
  ),
  team: (
    <>
      <circle cx="12" cy="8" r="3.25" />
      <path d="M5.5 20c.75-3.75 3-5.5 6.5-5.5s5.75 1.75 6.5 5.5" />
    </>
  ),
};

const BottomTabIcon = ({ icon }: { icon: MobileNavigationIcon }) => (
  <svg
    aria-hidden="true"
    className="bottom-tab-icon"
    data-bottom-tab-icon={icon}
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    {bottomTabIconPaths[icon]}
  </svg>
);

const scrollToTopAfterActiveTabClick = () => {
  window.requestAnimationFrame(() => {
    window.scrollTo(window.pageXOffset, 0);
    window.setTimeout(() => {
      window.scrollTo(window.pageXOffset, 0);
    }, 80);
  });
};

export const BottomTabBar = () => {
  const viewInfo = useViewData();
  const { lid, mobileTabAlerts, phase, playMenuOptions } = useLocalPartial([
    "lid",
    "mobileTabAlerts",
    "phase",
    "playMenuOptions",
  ]);
  const inLeague = viewInfo?.inLeague && lid !== undefined;
  const pageID: unknown = viewInfo?.idLoading ?? viewInfo?.idLoaded;
  const mobileReturnState =
    typeof window !== "undefined" ? parseMobileReturnState(window.location.search) : {};
  const preserveSourceTab =
    mobileReturnState.sourceSection !== undefined &&
    (mobileReturnState.archiveSeason !== undefined ||
      mobileReturnState.readonlyReason !== undefined ||
      mobileReturnState.sourcePage !== undefined);

  if (!inLeague) {
    return null;
  }

  const context: MobileNavigationContext = {
    hasPendingNegotiations: playMenuOptions.some(
      (option) => option.id === "contractNegotiationList",
    ),
    needsExpansionProtection: playMenuOptions.some((option) => option.id === "expansionDraft"),
    originSection: mobileReturnState.sourceSection,
    phase,
    preserveOriginSection: preserveSourceTab,
    playerTid:
      pageID === "player" && typeof viewInfo.data?.player?.tid === "number"
        ? viewInfo.data.player.tid
        : undefined,
  };
  const activeSection = resolveMobileSection(pageID, context);

  // N4-4: red dot = "a decision is waiting for you". Market comes from the
  // play-menu blockers already derived above; home/team come from the worker-
  // synced signals. Pure informational activity never lights a tab.
  const redDots = getMobileTabRedDots({
    hasPendingNegotiations: mobileTabAlerts.market,
    homeInboxDecision: mobileTabAlerts.home,
    needsExpansionProtection: context.needsExpansionProtection,
    teamRosterAlert: mobileTabAlerts.team,
  });

  return (
    <nav
      aria-label={i18n.t("tabBar.ariaLabel", {
        ns: "ui",
        defaultValue: "Bottom navigation",
      })}
      className="bottom-tab-bar fixed-bottom bg-light border-top"
      style={bottomTabBarStyle}
    >
      <ul className="nav nav-fill align-items-center mb-0">
        {mobileNavigationItems.map((tab) => {
          const active = activeSection === tab.key;
          const className = `nav-link text-center py-1 ${
            active ? "active fw-semibold" : "text-body-secondary"
          }`;
          const hasRedDot = redDots.has(tab.key);

          return (
            <li className="nav-item" key={tab.key}>
              <a
                aria-current={active ? "page" : undefined}
                className={className}
                href={helpers.leagueUrl(tab.resolveRoot(context))}
                onClick={active ? scrollToTopAfterActiveTabClick : undefined}
              >
                <span className="bottom-tab-icon-wrap">
                  <BottomTabIcon icon={tab.icon} />
                  {hasRedDot ? (
                    <span
                      aria-label={i18n.t("tabBar.pendingDecision", {
                        ns: "ui",
                        defaultValue: "Decision waiting",
                      })}
                      className="bottom-tab-red-dot"
                      data-testid={`bottom-tab-red-dot-${tab.key}`}
                      role="status"
                    />
                  ) : null}
                </span>
                <div style={{ fontSize: "0.75rem", lineHeight: 1.2 }}>
                  {i18n.t(tab.labelKey, {
                    ns: "ui",
                    defaultValue: tab.defaultLabel,
                  })}
                </div>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default BottomTabBar;
